import React, {useCallback, useEffect, useState} from 'react';
import {useAuth} from '../../../helpers/AuthContext';
import {CustomerMessage} from '../../CustomerMessage';
import './SellerMessages.css';

const responseError = (body, fallback) => body?.error || body?.message || fallback;

const formatDate = value => value ? new Date(value).toLocaleString() : '';

function MessageBubble({message, shopper}) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [linksEnabled, setLinksEnabled] = useState(false);
  const messageWithShopper = {...message, PhoneNumber: shopper || {nickname: 'Customer'}};

  return (
    <CustomerMessage
      message={messageWithShopper}
      loading={false}
      error={null}
      isRevealed={isRevealed}
      linksEnabled={linksEnabled}
      onReveal={() => setIsRevealed(true)}
      onEnableLinks={() => setLinksEnabled(true)}
      reportUrl={`/account/messages/report?messageId=${message.id}`}
    />
  );
}

export function SellerMessagesInbox({threads, loading, error, selectedCustomerId, onSelect}) {
  if (loading) return <p role="status">Loading conversations…</p>;
  if (error) return <p role="alert">{error}</p>;
  if (!threads.length) return <p>No customer conversations yet.</p>;

  return (
    <nav className="seller-messages__inbox" aria-label="Customer conversations">
      <h2>Conversations</h2>
      <ul className="seller-messages__thread-list">
        {threads.map(thread => {
          const selected = Number(thread.customerId) === Number(selectedCustomerId);
          const name = thread.shopper?.nickname || thread.shopper?.email || 'Customer';
          return (
            <li key={thread.customerId}>
              <button
                type="button"
                className={selected ? 'seller-messages__thread seller-messages__thread--selected' : 'seller-messages__thread'}
                aria-current={selected ? 'page' : undefined}
                onClick={() => onSelect(thread.customerId)}
              >
                <strong>{name}</strong>
                {thread.shopper?.email && <span>{thread.shopper.email}</span>}
                <span>{formatDate(thread.latestAt)}</span>
                <span>{thread.latestText}</span>
                {thread.hasSecurityWarning && <span>Contains a security warning</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// `readOnly` renders the history without the composer or its POST (Customer Directory read beta);
// the customer's name and contact status come from the host surface instead.
export function SellerMessageThread({customerId, onReplySent, readOnly = false}) {
  const {fetch: authFetch, API_URL} = useAuth();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(Boolean(customerId));
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  const loadThread = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_URL}/v1/seller/messages/threads/${customerId}`, {method: 'GET'});
      const body = await response.json();
      if (!response.ok) throw new Error(responseError(body, 'Failed to load conversation'));
      setThread(body?.data || null);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, authFetch, customerId]);

  useEffect(() => {
    setThread(null);
    setSendError('');
    setText('');
    loadThread();
  }, [loadThread]);

  const submit = async event => {
    event.preventDefault();
    const trimmedText = text.trim();
    if (!trimmedText || sending || !thread?.contactable) return;

    setSending(true);
    setSendError('');
    try {
      const response = await authFetch(
        `${API_URL}/v1/seller/messages/threads/${customerId}/reply`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({text: trimmedText}),
        },
      );
      const body = await response.json();
      if (!response.ok) throw new Error(responseError(body, 'Failed to send reply'));

      const message = body?.data?.message;
      if (message) {
        setThread(current => current ? {
          ...current,
          messages: [...current.messages, message],
        } : current);
      }
      setText('');
      onReplySent?.();
    } catch (submitError) {
      setSendError(submitError.message);
    } finally {
      setSending(false);
    }
  };

  if (!customerId) {
    return <div className="seller-messages__conversation"><p>Select a conversation to read it.</p></div>;
  }
  if (loading) return <div className="seller-messages__conversation"><p role="status">Loading conversation…</p></div>;
  if (error) {
    return (
      <div className="seller-messages__conversation">
        <p role="alert">{error}</p>
        {readOnly && <button type="button" onClick={loadThread}>Retry messages</button>}
      </div>
    );
  }
  if (!thread) return null;

  const customerName = thread.shopper?.nickname || thread.shopper?.email || 'Customer';
  return (
    <div
      className="seller-messages__conversation"
      role="region"
      aria-label={`Conversation with ${customerName}`}
    >
      {readOnly ? (
        <p>
          <a href={`/account/messages/${customerId}?uiVersion=2`}>Reply in Messages</a>
        </p>
      ) : (
        <>
          <header>
            <h2>{customerName}</h2>
            {thread.shopper?.email && <p>{thread.shopper.email}</p>}
          </header>
          {!thread.contactable && (
            <p className="seller-messages__contact-warning">
              This customer cannot receive email replies from this storefront.
            </p>
          )}
          <form className="seller-messages__composer" onSubmit={submit}>
            <label htmlFor="seller-message-reply">Reply to {customerName}</label>
            <textarea
              id="seller-message-reply"
              value={text}
              onChange={event => setText(event.target.value)}
              disabled={!thread.contactable || sending}
              placeholder={`Send ${customerName} a message...`}
            />
            {sendError && <p role="alert">{sendError}</p>}
            <button type="submit" disabled={!thread.contactable || !text.trim() || sending}>
              {sending ? 'Sending…' : 'Send email reply'}
            </button>
          </form>
        </>
      )}
      <ul className="messages-list seller-messages__message-list">
        {thread.messages.map(message => (
          <MessageBubble key={message.id} message={message} shopper={thread.shopper} />
        ))}
      </ul>
      {readOnly && !thread.messages.length && <p>No messages yet.</p>}
    </div>
  );
}

export default function SellerMessages({initialCustomerId = null}) {
  const {fetch: authFetch, API_URL} = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await authFetch(`${API_URL}/v1/seller/messages/threads`);
      const body = await response.json();
      if (!response.ok) throw new Error(responseError(body, 'Failed to load conversations'));
      setThreads(body?.data || []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, authFetch]);

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const selectCustomer = customerId => {
    setSelectedCustomerId(customerId);
    window.history.pushState({}, '', `/account/messages/${customerId}?uiVersion=2`);
  };

  return (
    <div className="seller-messages">
      <SellerMessagesInbox
        threads={threads}
        loading={loading}
        error={error}
        selectedCustomerId={selectedCustomerId}
        onSelect={selectCustomer}
      />
      <SellerMessageThread customerId={selectedCustomerId} onReplySent={loadThreads} />
    </div>
  );
}
